<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <Paciente
                :form="form.paciente"
                :model="model.paciente"
            />

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-select
                        :options="form.unidades"
                        :validations="validations.unidadeSolicitante"
                        label="Unidade solicitante"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeSolicitanteId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        :value="form.unidadeInternacao"
                        disabled
                        label="Unidade de internação"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :repository="repositories.cid"
                        :search-fields="fields.autocompletes.cidPrincial"
                        :validations="validations.cidPrincipal"
                        label="CID principal"
                        text-field="codigo"
                        v-model="model.cidPrincipal"
                    >
                        <template #itemLabel="{ item }">
                            {{ item.codigo | formatCid }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :repository="repositories.cid"
                        :search-fields="fields.autocompletes.cidSecundario"
                        :validations="validations.cidSecundario"
                        label="CID secundário"
                        text-field="codigo"
                        v-model="model.cidSecundario"
                    >
                        <template #itemLabel="{ item }">
                            {{ item.codigo | formatCid }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :repository="repositories.cid"
                        :search-fields="fields.autocompletes.cidCausasAssociadas"
                        :validations="validations.cidCausasAssociadas"
                        label="CID causas associadas"
                        text-field="codigo"
                        v-model="model.cidCausasAssociadas"
                    >
                        <template #itemLabel="{ item }">
                            {{ item.codigo | formatCid }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="9">
                    <rkm-form-input
                        :value="diagnosticoInicial"
                        disabled
                        label="Diagnóstico inicial"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :disabled="!model.pacienteId"
                        :validations="validations.faa"
                        label="Número da FAA"
                        readonly
                        v-model="model.faaId"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-button
                                :disabled="!model.pacienteId"
                                @click="$refs.ModalFaa.open()"
                                color="primary"
                                title="Vincular FAA existente"
                            >
                                <rkm-icon icon="link" />
                            </rkm-button>
                        </rkm-input-group-append>

                        <rkm-input-group-append slot="append">
                            <rkm-button
                                :disabled="!model.pacienteId"
                                @click="model.faaId = null"
                                color="outline-secondary"
                                title="Limpar número da FAA"
                            >
                                <rkm-icon icon="times" />
                            </rkm-button>
                        </rkm-input-group-append>
                    </rkm-form-input>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.principaisSinaisSintomasClinicos"
                        label="Principais sinais e sintomas clinicos"
                        rows="4"
                        v-model="model.principaisSinaisSintomasClinicos"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.condicoesQueJustificamInternacao"
                        label="Condições que justificam a internação"
                        rows="4"
                        v-model="model.condicoesQueJustificamInternacao"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.principaisResultadosDeProvasDiagnosticas"
                        label="Principais resultados de provas diagnósticas"
                        rows="4"
                        v-model="model.principaisResultadosDeProvasDiagnosticas"
                    />
                </rkm-col>
            </rkm-row>

            <Procedimento
                :additionals="additionals"
                :cid="model.cidPrincipal ? clone(model.cidPrincipal) : null"
                :form="form.procedimento"
                :model="model.procedimentos"
            />

            <CausaExterna
                :form="form.causaExterna"
                :model="model.causaExterna"
            />

            <Autorizacao
                :additionals="additionals"
                :form="form.autorizacao"
                :model="model.autorizacao"
            />

            <Desfecho :model="model" />
        </rkm-form>

        <ModalFaa
            @select="model.faaId = $event"
            :faas="form.faas"
            :nomePaciente="getSafely(() => form.paciente.identificacao.nomePaciente, '')"
            ref="ModalFaa"
        />

        <ModalImpressao ref="ModalImpressao" />
    </div>
</template>

<script>
import Paciente from './components/paciente/identificacao/Index'
import Procedimento from './components/procedimento/Index'
import CausaExterna from './components/causa-externa/Index'
import Autorizacao from './components/autorizacao/Index'
import Desfecho from './components/desfecho/Index'
import ModalFaa from './components/faa/Modal'
import ModalImpressao from './components/impressao/Modal'

import formatFilters from '@/filters/formats'

import tipoDocumento from '@/enums/cadastro/documentoTipo'

import { repository, request, object, data, stringCase, unmask } from '@/mixins'

export default {
    components: {
        Paciente,
        Procedimento,
        CausaExterna,
        Autorizacao,
        Desfecho,
        ModalFaa,
        ModalImpressao,
    },
    computed: {
        fields() {

            return {
                autocompletes: {
                    cidPrincial: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidSecundario: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                    cidCausasAssociadas: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                }
            }
        },
        validations() {

            return {
                unidadeSolicitante: { required: true },
                cidPrincipal: { required: true },
                cidSecundario: {},
                cidCausasAssociadas: {},
                faa: {},
                principaisSinaisSintomasClinicos: { required: true, max: 1000 },
                condicoesQueJustificamInternacao: { required: true, max: 1000 },
                principaisResultadosDeProvasDiagnosticas: { required: true, max: 1000 },
            }
        },
        diagnosticoInicial() {

            return this.model.cidPrincipal ? this.model.cidPrincipal.nome : ''
        },
    },
    created() {

        this.repositories = {
            cid: this.getConectaSusRepository('sigtap.cid'),
            aih: this.getConectaSusRepository('prontoAtendimento.aih')
        }
    },
    data: () => ({
        enums: { tipoDocumento }
    }),
    filters: {
        formatCid: formatFilters.formatCid,
    },
    methods: {
        save() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    (this.model.id
                        ? this.fetch(this.repositories.aih.update, this.model.id, this.prepareData())
                        : this.fetch(this.repositories.aih.store, this.prepareData())
                    )
                        .then(({ data }) => {

                            this.showSaveNotification()

                            this.$refs.ModalImpressao.open(data.id, data.impressoes, () => this.$emit('save', data))
                        })
                })
        },
        prepareData() {

            const model = this.clone(this.model)

            delete model.id

            if (!model.pacienteId) {

                model.paciente = {
                    sexoId: model.paciente.sexoId,
                    caracteristicasFisicas: model.paciente.caracteristicasFisicas
                }
            }

            this.createIdKeysToObject(model, [
                'cidPrincipal',
                'cidSecundario',
                'cidCausasAssociadas'
            ])

            model.procedimentos.forEach(procedimento => {

                procedimento.documentoNumero = this.unMaskDocumento(
                    procedimento.documentoTipoId,
                    procedimento.documentoNumero
                )

                this.createIdKeysToObject(procedimento, [
                    'procedimentoSolicitado',
                    'profissionalSolicitanteAssistente',
                ])

                this.formateDates(procedimento, [
                    'dataSolicitacao'
                ], 'DD/MM/YYYY')
            })

            model.causaExterna.cnpjSeguradora = this.unMaskCnpj(model.causaExterna.cnpjSeguradora)
            model.causaExterna.cnpjEmpresa = this.unMaskCnpj(model.causaExterna.cnpjEmpresa)

            model.autorizacao.documentoNumero = this.unMaskDocumento(
                model.autorizacao.documentoTipoId,
                model.autorizacao.documentoNumero
            )

            this.createIdKeysToObject(model.autorizacao, [
                'profissionalAutorizador',
                'profissionalSolicitanteAssistente',
            ])

            this.formateDates(model.autorizacao, [
                'dataAutorizacao'
            ], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(model)
        },
        unMaskDocumento(tipoId, numero) {

            switch (tipoId) {

                case this.enums.tipoDocumento.CNS: {

                    return this.unMaskCns(numero)
                }
                case this.enums.tipoDocumento.CPF: {

                    return this.unMaskCpf(numero)
                }
                default: {

                    return numero
                }
            }
        },
    },
    mixins: [repository, request, object, data, stringCase, unmask],
    props: {
        additionals: { type: Object, required: true },
        form: { type: Object, required: true },
        model: { type: Object, required: true },
    }
}
</script>